import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useCustomers } from '@/features/customers';
import { SearchableSelect, type SearchableOption } from '@/shared/components/searchable-select';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';

import { useCoupons } from '../api/coupons.queries';
import { formatCouponScope, formatCouponValue } from '../lib/coupon-format';

const NO_OPTIONS: SearchableOption[] = [];

/**
 * Pricing calculator: pick an active coupon of the country (and, for a coupon that belongs to one
 * customer, optionally that customer) to see what it would take off. Choosing nothing changes nothing.
 */
export function CouponPicker({
  countryId,
  couponId,
  customerId,
  onChange,
}: {
  countryId: number;
  couponId: number | null;
  customerId: number | null;
  onChange: (value: { couponId: number | null; customerId: number | null }) => void;
}) {
  const [search, setSearch] = useState('');
  const debounced = useDebouncedValue(search, 300);
  const coupons = useCoupons(countryId, {
    page: 1,
    pageSize: 20,
    status: 'active',
    search: debounced || undefined,
  });
  const selected = coupons.data?.items.find((c) => c.id === couponId);
  const personal = selected !== undefined && selected.kind !== 'Public';

  const [customerSearch, setCustomerSearch] = useState('');
  const debouncedCustomer = useDebouncedValue(customerSearch, 300);
  const customers = useCustomers({
    page: 1,
    pageSize: 20,
    search: debouncedCustomer || undefined,
    countryId,
    isActive: true,
  });

  const options =
    coupons.data?.items.map((c) => ({
      value: String(c.id),
      label: c.code,
      description: `${formatCouponValue(c, c.currency)} · ${formatCouponScope(c)}`,
    })) ?? NO_OPTIONS;
  const customerOptions =
    customers.data?.items.map((c) => ({
      value: String(c.id),
      label: c.name ?? c.userName ?? `#${c.id}`,
      description: c.msisdn ?? undefined,
    })) ?? NO_OPTIONS;

  return (
    <section
      aria-label="Coupon"
      className="border-border bg-card flex flex-col gap-3 rounded-lg border p-3 text-xs sm:flex-row sm:items-end"
    >
      <div className="flex-1 space-y-1">
        <Label htmlFor="calculator-coupon" className="text-xs">
          Coupon (optional)
        </Label>
        <SearchableSelect
          id="calculator-coupon"
          label="Coupon"
          value={couponId === null ? null : String(couponId)}
          selectedLabel={couponId === null ? null : (selected?.code ?? `Coupon #${couponId}`)}
          options={options}
          onChange={(value) => onChange({ couponId: Number(value), customerId: null })}
          placeholder="No coupon"
          searchPlaceholder="Search code…"
          emptyText="No active coupon matches."
          search={search}
          onSearchChange={setSearch}
          loading={coupons.isFetching}
          triggerClassName="h-9 w-full text-sm"
        />
      </div>
      {personal && (
        <div className="flex-1 space-y-1">
          <Label htmlFor="calculator-coupon-customer" className="text-xs">
            As customer (optional)
          </Label>
          <SearchableSelect
            id="calculator-coupon-customer"
            label="As customer"
            value={customerId === null ? null : String(customerId)}
            options={customerOptions}
            onChange={(value) => onChange({ couponId, customerId: Number(value) })}
            placeholder={
              selected?.customerName ? `Any (it is ${selected.customerName}'s)` : 'Any customer'
            }
            searchPlaceholder="Search name or mobile…"
            emptyText="No customer matches."
            search={customerSearch}
            onSearchChange={setCustomerSearch}
            loading={customers.isFetching}
            triggerClassName="h-9 w-full text-sm"
          />
        </div>
      )}
      {couponId !== null && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => onChange({ couponId: null, customerId: null })}
        >
          Remove coupon
        </Button>
      )}
    </section>
  );
}
